import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInAnonymously,
  signInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  linkWithCredential,
  EmailAuthProvider,
  type User,
  type ActionCodeSettings,
} from "firebase/auth";
import { apiUrl, webUrl } from "./config";



// Firebase configuration with environment variable validation
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebasestorage.app`,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Validate Firebase environment variables
function validateFirebaseConfig() {
  const requiredKeys = ['VITE_FIREBASE_API_KEY', 'VITE_FIREBASE_PROJECT_ID', 'VITE_FIREBASE_APP_ID'];
  const missingKeys = requiredKeys.filter(key => {
    const value = import.meta.env[key];
    return !value || value === 'undefined' || value === 'your-firebase-api-key' || value === 'your-firebase-project-id' || value === 'your-firebase-app-id';
  });

  if (missingKeys.length > 0) {
    console.warn('Firebase configuration incomplete. Missing or invalid:', missingKeys);
    return false;
  }

  return true;
}

const hasValidConfig = validateFirebaseConfig();



let app: any = null;
let auth: any = null;

// Initialize Firebase only if configuration is valid
if (hasValidConfig) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);

    // Set up auth persistence
    if (auth) {
      auth.settings = {
        appVerificationDisabledForTesting: import.meta.env.MODE === 'development'
      };
    }

    console.log('Firebase initialized successfully');
  } catch (error) {
    console.error('Firebase initialization failed:', error);
    auth = null;
    app = null;
  }
} else {
  console.warn('Firebase configuration incomplete - authentication will use fallback mode');
  console.log('To enable Firebase authentication, please:');
  console.log('1. Create a .env file in the project root');
  console.log('2. Add your Firebase configuration keys');
  console.log('3. Restart the development server');
}

export { auth, app };

/**
 * Returns the current user's Firebase ID token (JWT), or null if not signed in.
 * The token is cached by the SDK and auto-refreshed; getIdToken() is cheap to
 * call before each authenticated request. Works for anonymous users too.
 */
export const getIdToken = async (forceRefresh = false): Promise<string | null> => {
  try {
    const user = auth?.currentUser;
    if (!user) return null;
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.warn('Failed to get ID token:', error);
    return null;
  }
};

export const signInAnonymous = () => {
  if (!auth) {
    return Promise.reject(new Error('Firebase auth not initialized - check your environment configuration'));
  }

  console.log('Starting anonymous authentication...');
  return signInAnonymously(auth)
    .then((result) => {
      console.log('Anonymous authentication successful:', result.user.uid);
      return result;
    })
    .catch((error) => {
      console.error('Anonymous authentication failed:', error);
      throw error;
    });
};

export const onAuthStateChange = (callback: (user: User | null) => void) => {
  if (!auth) {
    console.log('Firebase auth not available, using fallback authentication');

    // Create a stable mock user for development/demo purposes
    // Use a fixed demo user ID for development to prevent constant re-auth
    const isDevelopment = import.meta.env.MODE === 'development';
    let demoUserId = localStorage.getItem('vyzle-chat-demo-user-id');

    if (!demoUserId) {
      // In development, use a fixed user ID
      // In production fallback, use timestamp-based ID
      demoUserId = isDevelopment
        ? 'demo_user_development'
        : `demo_user_${Date.now()}`;
      localStorage.setItem('vyzle-chat-demo-user-id', demoUserId);
    }

    const mockUser = {
      uid: demoUserId,
      displayName: localStorage.getItem('vyzle-chat-user-name') || 'Demo User',
      email: null,
      isAnonymous: true,
      providerId: 'anonymous'
    } as User;

    // Simulate async auth state change only once
    const hasTriggeredAuth = sessionStorage.getItem('vyzle-chat-auth-triggered');
    if (!hasTriggeredAuth) {
      sessionStorage.setItem('vyzle-chat-auth-triggered', 'true');

      setTimeout(() => {
        console.log('Using fallback authentication with stable demo user:', mockUser.uid);
        callback(mockUser);
      }, 100);
    } else {
      // Auth already triggered this session, return user immediately
      console.log('Reusing existing demo auth session:', mockUser.uid);
      callback(mockUser);
    }

    return () => {
      console.log('Unsubscribing from fallback auth state listener');
    };
  }

  console.log('Setting up Firebase auth state listener');
  return onAuthStateChanged(auth, (user) => {
    if (user) {
      console.log('Firebase auth state changed: authenticated', user.uid);
    } else {
      console.log('Firebase auth state changed: not authenticated');
    }
    callback(user);
  }, (error) => {
    console.error('Firebase auth state change error:', error);
    callback(null);
  });
};

// Check if Firebase is properly configured
export const isFirebaseConfigured = () => hasValidConfig && !!auth;

// Get current authentication status
export const getAuthStatus = () => ({
  isConfigured: isFirebaseConfigured(),
  hasAuth: !!auth,
  hasApp: !!app,
  environment: import.meta.env.MODE
});

// --- Account Auth (anonymous → email link for upgrade, or login for existing users) ---

export type AuthLinkMode = "upgrade" | "login";

/**
 * Send a magic-link email. The server detects whether the email belongs to
 * an existing registered user and sends either a login or upgrade email.
 * Falls back to Firebase client SDK (always upgrade mode) if server is unavailable.
 *
 * @returns the detected mode ('login' for existing users, 'upgrade' for new emails)
 */
export async function sendUpgradeEmail(email: string): Promise<{ mode: AuthLinkMode }> {
  if (!auth) throw new Error("Firebase auth not initialized");

  let mode: AuthLinkMode = "upgrade";

  try {
    // Prefer server-side: branded HTML email via Mailtrap + mode detection
    const res = await fetch(apiUrl("/api/auth/send-upgrade-link"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      // If server endpoint is not configured (503), fall back to client-side
      if (res.status === 503) throw new Error("fallback");
      throw new Error(data.error || "Failed to send sign-in link");
    }

    const data = await res.json().catch(() => ({}));
    if (data.mode === "login" || data.mode === "upgrade") {
      mode = data.mode;
    }
  } catch (err: any) {
    // Fallback: use Firebase client SDK (generic template, upgrade-only)
    if (err.message === "fallback" || err.name === "TypeError") {
      const actionCodeSettings: ActionCodeSettings = {
        url: webUrl("/app/auth/complete-upgrade"),
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      mode = "upgrade";
    } else {
      throw err;
    }
  }

  // Store email for retrieval when user clicks the link
  localStorage.setItem("vyzle-upgrade-email", email);
  return { mode };
}

/**
 * Complete the account upgrade by linking the email credential to the current anonymous user.
 * Returns the updated user or throws on failure.
 */
export async function completeUpgradeLink(url: string): Promise<User> {
  if (!auth) throw new Error("Firebase auth not initialized");
  if (!isSignInWithEmailLink(auth, url)) {
    throw new Error("Invalid or expired sign-in link");
  }

  const email = localStorage.getItem("vyzle-upgrade-email");
  if (!email) {
    throw new Error("Email not found. Please open this link on the same device where you started the upgrade.");
  }

  const credential = EmailAuthProvider.credentialWithLink(email, url);

  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("No active session. Please sign in first and try the upgrade again.");
  }

  const result = await linkWithCredential(currentUser, credential);
  // Clean up
  localStorage.removeItem("vyzle-upgrade-email");
  return result.user;
}

/**
 * Complete a login via email link — signs in an existing registered user.
 * If the current session is anonymous, Firebase will switch to the registered
 * account (the anonymous user's local data stays in localStorage but the UID
 * changes to the registered user's UID).
 */
export async function completeLoginLink(url: string): Promise<User> {
  if (!auth) throw new Error("Firebase auth not initialized");
  if (!isSignInWithEmailLink(auth, url)) {
    throw new Error("Invalid or expired sign-in link");
  }

  const email = localStorage.getItem("vyzle-upgrade-email");
  if (!email) {
    throw new Error("Email not found. Please open this link on the same device where you requested it.");
  }

  const result = await signInWithEmailLink(auth, email, url);
  // Clean up
  localStorage.removeItem("vyzle-upgrade-email");
  return result.user;
}

/** Check whether a URL is a Firebase email sign-in link. */
export function isUpgradeLink(url: string): boolean {
  if (!auth) return false;
  return isSignInWithEmailLink(auth, url);
}

/** Alias — same check works for both upgrade & login links. */
export const isAuthLink = isUpgradeLink;

export { type User };
